import { useEffect, useState, type CSSProperties } from 'react';
import type { Customer } from '@setapart/shared';
import { FONTS, COLORS } from '@setapart/shared';
import { getBrandToken } from '../lib/session';
import { API_BASE } from '../lib/api';

interface Props {
  customer: Customer;
  brandId: string;
  onDone: () => void;
}

interface Status {
  accountId: string | null;
  status: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  onboardedAt: number | null;
}

export function ConnectStripe({ customer: _customer, brandId, onDone }: Props) {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/brand/${brandId}/connect/status`, {
          credentials: 'include',
          headers: bearer(),
        });
        if (r.ok) setStatus((await r.json()) as Status);
      } catch (err) {
        setError((err as Error).message);
      }
    })();
  }, [brandId]);

  async function onConnect() {
    if (!termsAccepted) {
      setError('Please accept the Brand Partner Terms first.');
      return;
    }
    setError(null);
    setLaunching(true);
    try {
      const origin = window.location.origin;
      const res = await fetch(`${API_BASE}/api/brand/${brandId}/connect/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...bearer() },
        body: JSON.stringify({
          returnUrl: `${origin}/connect/stripe/return?brand_id=${brandId}`,
          refreshUrl: `${origin}/connect/stripe?brand_id=${brandId}`,
        }),
      });
      const body = (await res.json()) as { ok: boolean; url?: string; error?: string };
      if (body.ok && body.url) {
        window.location.href = body.url;
      } else {
        setError(body.error ?? 'Could not start onboarding');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLaunching(false);
    }
  }

  const connected = status?.chargesEnabled === true;

  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: '0.4em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.accent, marginBottom: 8 }}>
        PAYMENTS
      </div>
      <h1 style={{ fontFamily: FONTS.display, fontSize: 28, color: COLORS.ink, margin: 0, fontStyle: 'italic', fontWeight: 500 }}>
        Connect Stripe
      </h1>
      <p style={{ fontSize: 14, color: COLORS.muted, marginTop: 10, maxWidth: 540, lineHeight: 1.5 }}>
        Customers pay you directly through Stripe. Set Apart's commission is deducted at the
        source — no manual payouts, no chargeback exposure on our end. Onboarding takes ~5 min.
      </p>

      {status?.accountId && (
        <div style={statusBox}>
          <div style={statusRow}>
            <span style={statusLabel}>Account</span>
            <code style={{ fontSize: 11 }}>{status.accountId}</code>
          </div>
          <div style={statusRow}>
            <span style={statusLabel}>Status</span>
            <span style={{ color: connected ? '#1C3A2E' : COLORS.accent, fontWeight: 700, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {status.status}
            </span>
          </div>
          <div style={statusRow}>
            <span style={statusLabel}>Can accept payments</span>
            <span>{status.chargesEnabled ? '✓' : '—'}</span>
          </div>
          <div style={statusRow}>
            <span style={statusLabel}>Can receive payouts</span>
            <span>{status.payoutsEnabled ? '✓' : '—'}</span>
          </div>
        </div>
      )}

      <section style={termsBox} data-testid="brand-terms">
        <div style={{ fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.muted, marginBottom: 10 }}>
          BRAND PARTNER TERMS
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: COLORS.ink }}>
          <li>Set Apart receives a commission on each sale (rate per your current tier, visible in your dashboard).</li>
          <li>Stripe processes all payments. Set Apart never holds or transfers your funds manually.</li>
          <li>You will ship all orders within 3 business days of receipt.</li>
          <li>Refunds issued by Set Apart within 60 days may reverse your Stripe transfer automatically. Refunds after 60 days may be deducted from future payouts.</li>
          <li>Either party may terminate this arrangement with 30 days notice. Pending orders continue to fulfillment.</li>
          <li>You warrant you own the rights to sell every product you list.</li>
          <li>Disputes resolved first by good-faith discussion; then arbitration under Illinois law.</li>
        </ul>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, cursor: 'pointer' }}>
          <input
            data-testid="brand-terms-checkbox"
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span style={{ fontSize: 13, color: COLORS.ink }}>
            I agree to the Brand Partner Terms on behalf of my brand.
          </span>
        </label>
      </section>

      {error && <div style={{ color: COLORS.accent, fontSize: 12, marginTop: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button
          data-testid="brand-connect-stripe-start"
          onClick={onConnect}
          disabled={!termsAccepted || launching}
          style={{
            ...primaryBtn,
            opacity: !termsAccepted || launching ? 0.5 : 1,
            cursor: !termsAccepted || launching ? 'default' : 'pointer',
          }}
        >
          {launching ? 'Launching Stripe…' : connected ? 'Relaunch Stripe dashboard' : 'Continue to Stripe →'}
        </button>
        <button onClick={onDone} style={secondaryBtn}>← Back</button>
      </div>
    </div>
  );
}

function bearer(): Record<string, string> {
  const t = getBrandToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const statusBox: CSSProperties = {
  marginTop: 18,
  padding: 16,
  background: '#fff',
  border: `1px solid ${COLORS.sand}`,
  borderRadius: 14,
  maxWidth: 540,
};
const statusRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '6px 0',
  fontSize: 13,
  color: COLORS.ink,
};
const statusLabel: CSSProperties = {
  color: COLORS.muted,
  fontSize: 11,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  fontWeight: 700,
};
const termsBox: CSSProperties = {
  marginTop: 20,
  padding: 18,
  background: '#fff',
  border: `1px solid ${COLORS.sand}`,
  borderRadius: 14,
  maxWidth: 720,
};
const primaryBtn: CSSProperties = {
  padding: '12px 20px',
  borderRadius: 999,
  background: COLORS.ink,
  color: COLORS.cream,
  border: 'none',
  fontSize: 11,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  fontWeight: 700,
};
const secondaryBtn: CSSProperties = {
  padding: '12px 20px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: 11,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: COLORS.muted,
};
